import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {connect, useDispatch, useSelector} from "react-redux";
import QuestionBox from "./QuestionBox";
import {
    addNewQuestionAtIdx,
    changeSurvey,
    changeSurveyTitle, isSurveyEditRestrictedSelector,
    updateQuestionInSurveyAtIdx, activateEditModeAtIndex,
    changeSurveyId
} from "../../../../slices/surveyEditorSlice";
import SurveysService from "../../../../services/SurveysService";
import TitleBox from "./TitleBox";
import {
    addUISettingsToSurveyQuestions,
    defaultQuestion,
    defaultSurvey,
    findNextAvailableQid,
    getSurveyStatus,
    isSurveyEditRestricted,
    reorderChoicesArray,
    reorderChoicesValues
} from "../../../../utils/SurveyUtils";
import queryString from "query-string";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {validateSurvey} from "../../../../utils/SurveyValidationUtils";
import {withToastManager} from "react-toast-notifications";
import ErrorBoundary from "../../../meta/ErrorBoundary";
import * as BubblyUtils from "../../../../utils/BubblyUtils";
import SurveyAssistantChat from '../../chat/SurveyAssistantChat';

function SurveyEditor(props) {
    const location = useLocation();
    const dispatch = useDispatch();
    const surveyName = location.state && location.state.surveyName;
    const surveyId = props.match.params.surveyId;
    const {survey} = props;
    const editModeActive = !!survey.questions.find(q => q.editMode === true);
    const surveyEditRestricted = useSelector(isSurveyEditRestrictedSelector);

    const urlParams = queryString.parse(props.location.search);

    const [messagesList, setMessagesList] = useState([]);
    const [conversation, setConversation] = useState();
    const [isChatHidden, setIsChatHidden] = useState(true);

    const getDefaultMessage = () => { return { data: { type: "", text: ""}, error: false } }
    const convertServerMessageToChatMessageInfo = (message) => {
        let newMessageInfo = getDefaultMessage();
        newMessageInfo.data = message;
        return newMessageInfo;
    }
    const generateChatMessagesListFromServerMessagesList = (serverMessagesList) => {
        return serverMessagesList.map((message) => {
            return convertServerMessageToChatMessageInfo(message);
        });
    }

    useEffect(() => {
        if (!surveyId) {
            SurveysService.postSurveyTitle(surveyId, surveyName, (res) => {
                const resSurveyId = res.data;
                dispatch(changeSurveyId(resSurveyId));
                props.history.push('/survey/builder/' + resSurveyId);
            });
        }

        SurveysService.getOrCreateConversationForSurveyId(surveyId, null,
            (success) => {
                let uiConv = success.data;
                setConversation(uiConv);
                console.log("uiConv", uiConv);
                
                SurveysService.getConversationWithMessagesListForConversationId(uiConv.id, 
                    (success) => {
                        const {messages, survey} = success.data;

                        const chatMessages = generateChatMessagesListFromServerMessagesList(messages);
                        setMessagesList(chatMessages);
                    },
                    (error) => {
                        console.log("Error getConversationWithMessagesListForConversationId: ", error);
                    }
                );

            }, 
            (error) => {
                console.log("error: ", error);
            }
        );
    }, []);

    const handleGetSurvey = (survey) => {
        if (survey.type.localeCompare("template") === 0) {
            props.history.push("/surveys");
            props.toastManager.add("The page you are trying to access was not found", {
                appearance: 'warning'
            });
        }
        else if (isSurveyEditRestricted(survey)) {
            props.toastManager.add("Your survey status is " + getSurveyStatus(survey).toUpperCase() + " so you cannot reorder nor delete existing questions or their choices.", {
                appearance: 'warning'
            });
        }
        if (!survey.questions) {
            survey.questions = [{
                ...defaultQuestion,
                id: 'Q1'
            }];
        } else {
            addUISettingsToSurveyQuestions(survey);
        }
        //required to handle discard
        survey.defaultQuestions = survey.questions;

        dispatch(changeSurvey(survey));
        if(survey.questions.length === 1 & typeof urlParams.addQAfterFirstQuestion !== "undefined" && urlParams.addQAfterFirstQuestion === "1") {
            initNewQuestionAfterQuestionAtIndex(0);
        }
        else if (typeof urlParams.editQuestion !== "undefined") {
            const questionIndex = findArrayIndexFromQuestionId(urlParams.editQuestion);
            const questions = Array.from(survey.questions);
            if(typeof questionIndex !== "undefined") {
                scrollToQuestionFromPreview(questionIndex, questions[questionIndex])
            }
        }
    }

    useEffect(() => {
        if(surveyId) {
            SurveysService.getSurveyById(surveyId,
                (success) => {
                    handleGetSurvey(success.data)
                }, (error) => {
                    props.toastManager.add("Error! Cannot retrieve survey with id asdasdjaosdjas " + surveyId, {
                        appearance: 'error'
                    });
                }
            );
        } else {
            dispatch(changeSurvey(defaultSurvey));
        }
        if (surveyName) {
            dispatch(changeSurveyTitle(surveyName));
        }
    }, [location.pathname]);

    const initNewQuestionAfterQuestionAtIndex = (index) => {
        let availableQId = findNextAvailableQid(props.survey.questions);
        props.addNewQuestionAtIdx(index + 1, availableQId);
    };

    const findArrayIndexFromQuestionId  = (questionId) => {
        const index = props.survey.questions.findIndex(element => element.id === questionId);
        return index === -1 ? undefined : index;
    };

    const scrollToQuestionFromPreview = (questionIndex, questionData) => {
        const node = document.getElementById("question_box_id_" + questionIndex);
        if(typeof node !== 'undefined'){
            window.scrollTo({ behavior: 'smooth', top: node.offsetTop });
            props.activateEditModeAtIndex(questionIndex, questionData);
        }
    };

    const scrollIntoViewHandler = (index, message) => {
        const node = document.getElementById("question_box_id_" + index);
        if(typeof node !== 'undefined'){
            window.scrollTo({ behavior: 'smooth', top: node.offsetTop });
            if (message) {
                props.toastManager.add(message, {
                    appearance: 'error'
                });
            }
        }
    };

    const renderQuestionsList = (questions) => {
        let htmlQuestionsList;
        if(!editModeActive && !surveyEditRestricted) {
            htmlQuestionsList = questions.map((question, idx) => {
                return (
                    <Draggable class="draggable" draggableId={idx + "_draggables"} index={idx}
                               key={"draggable_question_item_key_" + idx}>
                        {(provided) => (
                            <div ref={provided.innerRef}
                                 {...provided.draggableProps}
                                 {...provided.dragHandleProps} key={idx + "_key"}>
                                <QuestionBox
                                    questionData={question}
                                    idx={idx}
                                    scrollIntoViewHandler={scrollIntoViewHandler}
                                />
                            </div>
                        )}
                    </Draggable>
                )
            });
        }else{
            htmlQuestionsList = questions.map((question, idx) => {
                return (
                    <QuestionBox
                        questionData={question}
                        idx={idx}
                        scrollIntoViewHandler={scrollIntoViewHandler}
                        key={"question_item_" + idx}
                    />
                )});
        }
        return (
            <React.Fragment>
                {htmlQuestionsList}
            </React.Fragment>
        );
    };

    const handleSurveySaveAfterDragEnd = (survey) => {

        survey = JSON.parse(JSON.stringify(survey));

        //only surveys of type test allow the merge of the questions
        survey.type = 'test';
        let isSurveyValid = validateSurvey(survey);
        if (!isSurveyValid) return;

        SurveysService.saveSurvey(survey,
            (success1) => {
                    SurveysService.getSurveyById(survey.id,
                        (success2) => {
                            let freshSurvey = success2.data;
                            addUISettingsToSurveyQuestions(freshSurvey);
                            freshSurvey.defaultQuestions = freshSurvey.questions;
                            dispatch(changeSurvey(freshSurvey));
                        },
                        (error2) => {
                            props.toastManager.add("Survey refresh after drag save error!", {
                                appearance: 'error'
                            });
                        });
            },
            (error1) => {
                props.toastManager.add("Error saving existing edit mode question", {
                    appearance: 'error'
                });
            }
        );
    };

    const onDragEnd = (e) => {
        let survey = JSON.parse(JSON.stringify(props.survey)); 

        if(e.destination.droppableId.localeCompare("droppable_question") === 0){
            //required to persist the data
            BubblyUtils.arrayMove(survey.questions, e.source.index, e.destination.index);

            //required to update the data into the redux store
            dispatch(changeSurvey(survey));
            handleSurveySaveAfterDragEnd(survey);
        }
        else {
            let questionIndex = survey.questions.findIndex(q => q.id === e.type);
            let question = survey.questions[questionIndex];
            const sourceIdx = e.source.index;
            const destinationIdx = e.destination.index;

            switch(e.destination.droppableId) {
                case "droppable_choice":
                    reorderChoicesArray(question.choices, sourceIdx, destinationIdx);
                    reorderChoicesValues(question.choices);
                    break;
                case "droppable_select":
                    reorderChoicesArray(question.selects, sourceIdx, destinationIdx);
                    break;
                case "droppable_option":
                    question.selects.forEach(select => {
                        reorderChoicesArray(select.options, sourceIdx, destinationIdx);
                        reorderChoicesValues(select.options);
                    });
                    break;
            }

            dispatch(updateQuestionInSurveyAtIdx({
                idx: questionIndex,
                questionData: question
            }));
        }

    };

    const addMessage = (message) => {
        setMessagesList([...messagesList, message]);
    }

    const addMessages = (messages) => {
        setMessagesList([...messagesList, ...messages]);
    }

    return (
        <div className="questionaire__builder">
            <ErrorBoundary boundaryName="Survey Editor Component">
                <TitleBox />
                <div className="questionaire__builder__title questionaire__builder__title--mobile">
                    Builder
                    <span className="questionaire__builder__title__buble questionaire__builder__title__buble--red"></span>
                </div>
                <div className="d-flex">
                    <div style={{
                        position: "fixed",
                        bottom: "0px",
                        right: "0px",
                        zIndex: 2,
                        }}
                        hidden={!isChatHidden}
                    >
                        <img
                            src="/assets/img/site/logo-avatar.png" width="100" height="100" className="avatar-img me-2 mb-2" alt="avatar"
                            style={{ borderRadius: "50%", border: "1px solid #00000047", cursor: "pointer" }}
                            onClick={() => {setIsChatHidden(false); console.log("Wow")}}
                        />
                    </div>
                    <div
                        style={{
                            position: "fixed",
                            bottom: "0px",
                            right: "0px",
                            zIndex: 1,
                            background: "#f7f7f7",
                            border: "2px solid #00000036",
                            borderRadius: "1em 1em 0 0",
                            width: "30%",
                            marginRight: "1em",
                        }}
                        hidden={isChatHidden}
                    >
                        <div className="d-flex ps-3 justify-content-between border-bottom border-1">
                            <div className="">
                                <img src="/assets/img/site/logo-avatar.png" width="35" height="35" className="avatar-img me-2" alt="avatar"/>
                                <span>Quisito your AI assistant</span>
                            </div>
                            <button
                                className="btn"
                                onClick={() => setIsChatHidden(true)}
                            >
                                    Close
                            </button>
                        </div>
                        <SurveyAssistantChat
                            conversation={conversation}
                            addMessage={addMessage}
                            addMessages={addMessages}
                            messagesList={messagesList}
                            handleGetSurvey={handleGetSurvey}
                            surveyId={surveyId}
                            />
                    </div>
                    <div className="w-100">
                        <ErrorBoundary boundaryName="Questions List">
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId="droppable_question" class="droppable_question">
                                        {(provided, snapshot) => (
                                            <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            >
                                                {renderQuestionsList(survey.questions)}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                </Droppable>
                            </DragDropContext>
                        </ErrorBoundary>
                    </div>
                </div>
            </ErrorBoundary>
        </div>
    )
}

function mapStateToProps(state) {
    const {survey} = state;
    return {survey};
}

const mapDispatchToProps = {addNewQuestionAtIdx, activateEditModeAtIndex};

export default connect(mapStateToProps, mapDispatchToProps)(withToastManager(SurveyEditor));